import React, { useState, useEffect, useRef } from 'react';
import {
  apiService,
  type ApiKeyRole,
  type CreateApiKeyRequest,
  type CreateApiKeyResponse,
  type ApiKeyListItem,
  type ListApiKeysRequest,
  type PaginationResponse,
  type DashboardMetrics,
  type RecentRequest,
  type RecentRequestsRequest,
  type RecentRequestsFilter,
  type TrustQualityMetrics,
  type RequestOutcomesResponse,
  type RequestOutcomesRequest
} from '../services/api';
import './EnterpriseConsumptionConsole.css';

interface ConsumptionRow {
  accessed_at: string;
  caller_id: string;
  end_user_email: string;
  role: string;
  caller_role: string;
  caller_type: string;
  resource_type: string;
  resource_ids: string;
  http_method: string;
  endpoint: string;
  records_returned: number;
  trust_score: number | null;
  trust_level: 'high' | 'medium' | 'low' | null;
  status_code: number;
  response_time_ms: number;
  correlation_id: string;
  query_filters: string;
  query_complexity_score: number;
  error_message: string;
  ip_address: string;
  user_agent: string;
}

// Use ApiKeyListItem from API service instead of local Consumer interface
type Consumer = ApiKeyListItem;

interface NewConsumerForm {
  name: string;
  description: string;
  callerRole: string;
  ownerEmail: string;
  expiresInDays: string;
  dailyQuota: string;
  organization: string;
  environment: string;
  contactPerson: string;
}

const EnterpriseConsumptionConsole: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedConsumers, setExpandedConsumers] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState({ role: '', trust: '', status: '' });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isInitialMount = useRef(true);
  const hasFetched = useRef(false);
  const [consumerList, setConsumerList] = useState<Consumer[]>([]);
  const [consumersLoading, setConsumersLoading] = useState(false);
  const [consumersError, setConsumersError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationResponse>({
    page: 1,
    page_size: 10,
    total_records: 0,
    total_pages: 1,
  });

  const [newConsumerForm, setNewConsumerForm] = useState<NewConsumerForm>({
    name: '',
    description: '',
    callerRole: '',
    ownerEmail: '',
    expiresInDays: '365',
    dailyQuota: '',
    organization: '',
    environment: 'production',
    contactPerson: ''
  });

  // API Key Roles management
  const [apiKeyRoles, setApiKeyRoles] = useState<ApiKeyRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // API Key Creation management
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [createdApiKey, setCreatedApiKey] = useState<CreateApiKeyResponse | null>(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Dashboard Metrics management
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  // Recent Requests management
  const [recentRequests, setRecentRequests] = useState<RecentRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [requestsPagination, setRequestsPagination] = useState<PaginationResponse>({
    page: 1,
    page_size: 10,
    total_records: 0,
    total_pages: 1,
  });

  // Trust Quality management
  const [trustQualityMetrics, setTrustQualityMetrics] = useState<TrustQualityMetrics | null>(null);
  const [trustQualityLoading, setTrustQualityLoading] = useState(false);
  const [trustQualityError, setTrustQualityError] = useState<string | null>(null);

  // Request Outcomes management
  const [requestOutcomes, setRequestOutcomes] = useState<RequestOutcomesResponse | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);
  const [outcomesPagination, setOutcomesPagination] = useState<PaginationResponse>({
    page: 1,
    page_size: 10,
    total_records: 0,
    total_pages: 1,
  });
  const [outcomesStatusFilter, setOutcomesStatusFilter] = useState<string>('');

  // Global date range management - used by all API sections
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Global date range - default to yesterday to today for better data visibility
  const [globalDateRange, setGlobalDateRange] = useState({
    startDate: yesterday,
    endDate: today,
  });

  // Global refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Hover popup state for professional tooltips
  const [hoverPopup, setHoverPopup] = useState<{
    show: boolean;
    text: string;
    x: number;
    y: number;
  }>({
    show: false,
    text: '',
    x: 0,
    y: 0,
  });

  // Fetch API keys from the server
  const fetchApiKeys = async (page: number = 1) => {
    if (consumersLoading) return; // Prevent duplicate calls

    setConsumersLoading(true);
    setConsumersError(null);

    try {
      const request: ListApiKeysRequest = {
        filters: {}, // No filters for now - show all keys
        pagination: {
          page,
          page_size: 10,
        },
        sort: [
          {
            field: 'created_at',
            direction: 'desc',
          },
        ],
      };

      const response = await apiService.listApiKeys(request);
      setConsumerList(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      setConsumersError(error instanceof Error ? error.message : 'Failed to load API keys');
      setConsumerList([]); // Clear list on error
    } finally {
      setConsumersLoading(false);
    }
  };

  // Fetch dashboard metrics from the server
  const fetchDashboardMetrics = async () => {
    if (metricsLoading) return; // Prevent duplicate calls

    setMetricsLoading(true);
    setMetricsError(null);

    try {
      const metrics = await apiService.getDashboardMetrics(
        globalDateRange.startDate,
        globalDateRange.endDate
      );
      setDashboardMetrics(metrics);
    } catch (error) {
      console.error('Failed to fetch dashboard metrics:', error);
      setMetricsError(error instanceof Error ? error.message : 'Failed to load dashboard metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  // Fetch recent requests from the server
  const fetchRecentRequests = async (page: number = 1, currentFilters?: RecentRequestsFilter) => {
    if (requestsLoading) return; // Prevent duplicate calls

    setRequestsLoading(true);
    setRequestsError(null);

    try {
      const request: RecentRequestsRequest = {
        filters: currentFilters || {
          role: filters.role || undefined,
          trust_level: filters.trust || undefined,
          status: filters.status || undefined,
          start_date: globalDateRange.startDate,
          end_date: globalDateRange.endDate,
        },
        page,
        page_size: 10,
      };

      const response = await apiService.getRecentRequests(request);
      setRecentRequests(response.data);
      setRequestsPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch recent requests:', error);
      setRequestsError(error instanceof Error ? error.message : 'Failed to load recent requests');
      setRecentRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  // Fetch trust quality metrics from the server
  const fetchTrustQualityMetrics = async () => {
    if (trustQualityLoading) return; // Prevent duplicate calls

    setTrustQualityLoading(true);
    setTrustQualityError(null);

    try {
      const metrics = await apiService.getTrustQualityMetrics(
        globalDateRange.startDate,
        globalDateRange.endDate
      );
      setTrustQualityMetrics(metrics);
    } catch (error) {
      console.error('Failed to fetch trust quality metrics:', error);
      setTrustQualityError(error instanceof Error ? error.message : 'Failed to load trust quality metrics');
    } finally {
      setTrustQualityLoading(false);
    }
  };

  // Fetch request outcomes from the server
  const fetchRequestOutcomes = async (page: number = 1, statusFilter?: string) => {
    if (outcomesLoading) return; // Prevent duplicate calls

    setOutcomesLoading(true);
    setOutcomesError(null);

    try {
      const request: RequestOutcomesRequest = {
        page,
        page_size: 10,
        status_filter: statusFilter || outcomesStatusFilter || undefined,
        start_date: globalDateRange.startDate,
        end_date: globalDateRange.endDate,
      };

      const response = await apiService.getRequestOutcomes(request);
      setRequestOutcomes(response);
      setOutcomesPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch request outcomes:', error);
      setOutcomesError(error instanceof Error ? error.message : 'Failed to load request outcomes');
    } finally {
      setOutcomesLoading(false);
    }
  };

  // Load all initial data on component mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadInitialData = async () => {
      // Load all data in parallel on initial mount
      await Promise.allSettled([
        fetchApiKeys(),
        fetchDashboardMetrics(),
        fetchTrustQualityMetrics(),
        fetchRecentRequests(),
        fetchRequestOutcomes(),
        fetchApiKeyRoles(), // Add this to load roles for the filter dropdown
      ]);
      // Mark that initial mount is complete after all calls
      isInitialMount.current = false;
    };

    loadInitialData();
  }, []);

  // Refetch recent requests when filters change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) return; // Skip on initial mount

    const apiFilters: RecentRequestsFilter = {
      role: filters.role || undefined,
      trust_level: filters.trust || undefined,
      status: filters.status || undefined,
      start_date: globalDateRange.startDate,
      end_date: globalDateRange.endDate,
    };
    setExpandedRows(new Set()); // Clear expanded rows when filters change
    fetchRecentRequests(1, apiFilters);
  }, [filters.role, filters.trust, filters.status]);

  // Refetch request outcomes when status filter changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) return; // Skip on initial mount

    fetchRequestOutcomes(1, outcomesStatusFilter);
  }, [outcomesStatusFilter]);

  // Refetch ALL APIs when global date range changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) return; // Skip on initial mount

    // Trigger all API calls simultaneously when date range changes
    const loadAllData = async () => {
      await Promise.allSettled([
        fetchDashboardMetrics(),
        fetchTrustQualityMetrics(),
        fetchRecentRequests(1), // Reset to first page
        fetchRequestOutcomes(1, outcomesStatusFilter), // Reset to first page
      ]);
    };

    // Clear expanded rows when date changes
    setExpandedRows(new Set());
    loadAllData();
  }, [globalDateRange.startDate, globalDateRange.endDate]);

  // Refresh all dashboard data
  const refreshAllData = async () => {
    if (isRefreshing) return; // Prevent duplicate refresh calls

    setIsRefreshing(true);

    try {
      // Refresh all data in parallel
      await Promise.allSettled([
        fetchApiKeys(1), // Reset to first page
        fetchDashboardMetrics(),
        fetchTrustQualityMetrics(),
        fetchRecentRequests(1), // Reset to first page
        fetchRequestOutcomes(1, outcomesStatusFilter), // Reset to first page
        fetchApiKeyRoles(),
      ]);

      // Clear expanded rows on refresh
      setExpandedRows(new Set());
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const consumerColors: Record<string, { bg: string; fg: string; i: string }> = {
    'Clarity': { bg:'#e8f0fe', fg:'#1565c0', i:'CL' },
    'Salesforce CRM': { bg:'#fef3c7', fg:'#b45309', i:'SF' },
    'Executive Reporting Dashboard': { bg:'#e8f7ef', fg:'#1a7f4e', i:'ER' },
    'Compliance Review Workflow': { bg:'#fdecea', fg:'#c0392b', i:'CR' },
    'Advisor Desktop': { bg:'#eef1f6', fg:'#6b7a8d', i:'AD' },
    'Risk Monitoring Service': { bg:'#f3e8ff', fg:'#6d28d9', i:'RM' },
  };

  const resourceLabels: Record<string, string> = {
    clients: 'Unified Client Profile',
    holdings: 'Holdings Detail',
    portfolio_summaries: 'Portfolio Summary',
    accounts: 'Account Detail',
    converge_data: 'Client Converge Data',
  };

  const roleLabels: Record<string, string> = {
    ai_consumer: 'AI Agent',
    financial_advisor: 'Advisor Platform',
    executive_reporting: 'Reporting Platform',
    compliance_officer: 'Compliance Workflow',
    risk_analyst: 'Risk Monitoring',
    client_converge: 'Client Converge',
  };

  const toggleRowById = (rowId: string) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(rowId)) {
      newSet.delete(rowId);
    } else {
      newSet.add(rowId);
    }
    setExpandedRows(newSet);
  };

  const toggleConsumer = (idx: number) => {
    const newSet = new Set(expandedConsumers);
    if (newSet.has(idx)) {
      newSet.delete(idx);
    } else {
      newSet.add(idx);
    }
    setExpandedConsumers(newSet);
  };

  const getAvatarStyle = (name: string) => {
    return consumerColors[name] || { bg:'#e8f0fe', fg:'#1565c0', i: name.substring(0,2).toUpperCase() };
  };

  const statusBadge = (code: number) => {
    if (code === 200) return <span className="badge b-success">Successful</span>;
    if (code === 403) return <span className="badge b-danger">Restricted</span>;
    if (code === 429) return <span className="badge b-warn">Rate Limited</span>;
    if (code === 500) return <span className="badge b-danger">Error</span>;
    return <span className="badge b-neutral">{code}</span>;
  };

  const trustBadge = (level: string | null) => {
    if (!level) return <span className="badge b-neutral">—</span>;
    const cls = level === 'high' ? 'b-t-high' : level === 'medium' ? 'b-t-med' : 'b-t-low';
    const label = level === 'high' ? 'High' : level === 'medium' ? 'Medium' : 'Low';
    return <span className={`badge ${cls}`}>{label}</span>;
  };

  const consumerStatusBadge = (status: string) => {
    const map: Record<string, { cls: string; dot: string; label: string }> = {
      'Active': { cls:'b-success', dot:'sdot-active', label:'Active' },
      'Expiring Soon': { cls:'b-warn', dot:'sdot-expiring', label:'Expiring Soon' },
      'Expired': { cls:'b-danger', dot:'sdot-expired', label:'Expired' },
    };
    const s = map[status] || { cls:'b-neutral', dot:'sdot-pending', label: status };
    return <><span className={`sdot ${s.dot}`}></span><span className={`badge ${s.cls}`}>{s.label}</span></>;
  };

  const expirationStatus = (expiresAt: string) => {
    if (!expiresAt || expiresAt === '—') return 'Active';
    const today = new Date('2026-05-15T00:00:00');
    const exp = new Date(expiresAt + 'T00:00:00');
    const days = Math.ceil((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days <= 30) return 'Expiring Soon';
    return 'Active';
  };

  const consumerDisplayName = (consumer: Consumer) => {
    // Use the name from API response directly, fallback to formatted key_id
    return consumer.name || consumer.key_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Helper function to format dates consistently in US format
  const formatUSDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '—';

    try {
      let date: Date;

      // Handle time-only strings (like "08:16:28")
      if (/^\d{2}:\d{2}:\d{2}$/.test(dateString.trim())) {
        // Add today's date to time-only strings
        const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD
        date = new Date(`${today}T${dateString}`);
      } else {
        // Handle full datetime strings
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }

      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString; // Return original if formatting fails
    }
  };

  // Helper function for text truncation with professional hover tooltip
  const truncateText = (text: string | null | undefined, maxLength: number = 20) => {
    const safeText = text || '';

    // If text is short enough, return as-is
    if (safeText.length <= maxLength) {
      return <span>{safeText}</span>;
    }

    const handleMouseEnter = (e: React.MouseEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverPopup({
        show: true,
        text: safeText,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    };

    const handleMouseLeave = () => {
      setHoverPopup(prev => ({ ...prev, show: false }));
    };

    return (
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: 'pointer'
        }}
      >
        {safeText.substring(0, maxLength)}...
      </span>
    );
  };

  // Transform API data to UI format
  const transformRecentRequestToRow = (request: RecentRequest): ConsumptionRow => {
    // Parse response time (e.g., "142ms" -> 142)
    const responseTimeMs = parseInt(request.response_time.replace('ms', '')) || 0;

    // Map trust level
    const trustLevel = request.trust_level.toLowerCase().includes('high') ? 'high'
                      : request.trust_level.toLowerCase().includes('medium') ? 'medium'
                      : request.trust_level.toLowerCase().includes('low') ? 'low'
                      : null;

    // Map status to status code
    const statusCode = request.status === 'Successful' ? 200
                      : request.status === 'Failed' ? 500
                      : request.status === 'Restricted' ? 403
                      : request.status === 'Rate Limited' ? 429
                      : 500;

    return {
      accessed_at: (request as any).accessed_at || request.time,
      caller_id: request.consumer,
      end_user_email: `${request.consumer.toLowerCase().replace(/\s+/g, '-')}@internal`,
      role: request.role,
      caller_role: request.role,
      caller_type: request.caller_type,
      resource_type: request.data_requested.toLowerCase().includes('client') ? 'clients'
                    : request.data_requested.toLowerCase().includes('portfolio') ? 'portfolio_summaries'
                    : request.data_requested.toLowerCase().includes('account') ? 'accounts'
                    : request.data_requested.toLowerCase().includes('holdings') ? 'holdings'
                    : 'converge_data',
      resource_ids: request.data_requested.match(/\(([^)]+)\)/)?.[1] || '',
      http_method: 'GET',
      endpoint: request.endpoint,
      records_returned: request.records_returned,
      trust_score: request.trust_score / 100, // Convert from 0-100 to 0-1
      trust_level: trustLevel,
      status_code: statusCode,
      response_time_ms: responseTimeMs,
      correlation_id: request.correlation_id,
      query_filters: request.query_filters || 'No filters applied',
      query_complexity_score: request.query_complexity,
      error_message: request.error_message || '',
      ip_address: request.ip_address,
      user_agent: request.user_agent,
    };
  };

  // Pagination is now handled by the API

  // Pagination component
  const PaginationControls = ({
    currentPage,
    totalPages,
    onPageChange
  }: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  }) => {
    // Always show pagination info, but hide controls if only 1 page
    if (totalPages <= 1) {
      return (
        <div className="pagination-controls">
          <span style={{fontSize: '12px', color: 'var(--muted)'}}>
            Page 1 of 1
          </span>
        </div>
      );
    }

    const pages = [];
    const showPages = 5; // Show 5 page numbers
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls">
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        <div className="pagination-numbers">
          {pages.map(page => (
            <button
              key={page}
              className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          className="btn btn-sm btn-ghost"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };


  // Transform API data to UI format
  const transformedRows = recentRequests.map(transformRecentRequestToRow);

  // Get paginated data for tables
  const paginatedConsumptionRows = transformedRows; // API already handles pagination

  // Fetch API key roles
  const fetchApiKeyRoles = async () => {
    if (rolesLoading || apiKeyRoles.length > 0) return; // Prevent duplicate calls and don't fetch if already loaded

    setRolesLoading(true);
    setRolesError(null);

    try {
      const roles = await apiService.getApiKeyRoles();
      setApiKeyRoles(roles);
    } catch (error) {
      console.error('Failed to fetch API key roles:', error);
      setRolesError(error instanceof Error ? error.message : 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  // Drawer management functions
  const openDrawer = async () => {
    setIsDrawerOpen(true);
    document.body.style.overflow = 'hidden';

    // Fetch API key roles when drawer opens
    await fetchApiKeyRoles();
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    document.body.style.overflow = '';

    // Reset form and state
    setNewConsumerForm({
      name: '',
      description: '',
      callerRole: '',
      ownerEmail: '',
      expiresInDays: '365',
      dailyQuota: '',
      organization: '',
      environment: 'production',
      contactPerson: ''
    });

    // Clear API response states
    setCreatedApiKey(null);
    setCreationError(null);
    setIsCreating(false);
    setShowApiKeyModal(false);
  };

  const closeApiKeyModal = () => {
    setShowApiKeyModal(false);
    setCreatedApiKey(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeDrawer();
    }
  };

  const registerConsumer = async () => {
    const { name, description, callerRole, ownerEmail, expiresInDays, dailyQuota, organization, environment, contactPerson } = newConsumerForm;

    // Basic validation
    if (!name.trim()) {
      alert('Consumer Name is required.');
      return;
    }
    if (!description.trim()) {
      alert('Description is required.');
      return;
    }
    if (!callerRole) {
      alert('Caller Role is required.');
      return;
    }
    if (!ownerEmail.trim()) {
      alert('Owner Email is required.');
      return;
    }
    if (!organization.trim()) {
      alert('Organization is required.');
      return;
    }
    if (!dailyQuota || isNaN(Number(dailyQuota))) {
      alert('Daily Limit must be a valid number.');
      return;
    }
    if (!expiresInDays || isNaN(Number(expiresInDays)) || Number(expiresInDays) < 1 || Number(expiresInDays) > 3650) {
      alert('Expires In Days must be a number between 1 and 3650.');
      return;
    }

    // Clear any previous errors
    setCreationError(null);
    setIsCreating(true);

    try {
      // Prepare API request
      const request: CreateApiKeyRequest = {
        name,
        description,
        role_name: callerRole,
        owner_email: ownerEmail,
        expires_in_days: Number(expiresInDays),
        daily_quota: Number(dailyQuota),
        created_by: 'Admin',
        key_metadata: {
          organization,
          environment,
          contact_person: contactPerson || undefined,
        },
      };

      // Call API to create the key
      const response = await apiService.createApiKey(request);

      // Store the response for display
      setCreatedApiKey(response);

      // Add to consumer list for immediate display
      const newConsumer: Consumer = {
        id: response.data.api_key.id,
        key_id: response.data.api_key.key_id,
        name: response.data.api_key.name,
        description: description,
        role_name: response.data.api_key.role_name,
        owner_email: ownerEmail,
        status: response.data.api_key.status,
        created_by: 'Admin',
        approved_by: 'Admin',
        approved_at: new Date().toISOString(),
        last_used_at: null,
        expires_at: response.data.api_key.expires_at,
        daily_quota: response.data.api_key.daily_quota,
        key_metadata: {
          organization,
          environment,
          contact_person: contactPerson,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setConsumerList(prev => [newConsumer, ...prev]);

      // Close the drawer and show API key modal
      setIsDrawerOpen(false);
      document.body.style.overflow = '';

      // Reset form but don't reset showApiKeyModal here
      setNewConsumerForm({
        name: '',
        description: '',
        callerRole: '',
        ownerEmail: '',
        expiresInDays: '365',
        dailyQuota: '',
        organization: '',
        environment: 'production',
        contactPerson: ''
      });

      setCreationError(null);
      setIsCreating(false);

      // Show the API key modal
      setShowApiKeyModal(true);

    } catch (error) {
      console.error('Failed to create API key:', error);
      setCreationError(error instanceof Error ? error.message : 'Failed to create API key');
      setIsCreating(false);
      return; // Don't close drawer on error
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <div className="page">
        {/* KPI Strip */}
        <div className="section-label">
          <span>Governance Dashboard</span>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: 'var(--muted)', marginTop: '6px'}}>
            <span style={{fontWeight: 600, color: 'var(--text)'}}>DATE RANGE:</span>
            <input
              type="date"
              value={globalDateRange.startDate}
              onChange={(e) => setGlobalDateRange(prev => ({...prev, startDate: e.target.value}))}
              style={{
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '12px',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontWeight: 500,
              }}
            />
            <span style={{fontWeight: 600, color: 'var(--accent)'}}>TO</span>
            <input
              type="date"
              value={globalDateRange.endDate}
              onChange={(e) => setGlobalDateRange(prev => ({...prev, endDate: e.target.value}))}
              style={{
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '12px',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontWeight: 500,
              }}
            />
            <button
              onClick={refreshAllData}
              disabled={isRefreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                background: isRefreshing ? 'var(--muted-bg)' : 'var(--surface)',
                color: isRefreshing ? 'var(--muted)' : 'var(--accent)',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              title="Refresh all dashboard data"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 1s ease-in-out',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                }}
              >
                <path
                  d="M10 6C10 8.21 8.21 10 6 10C3.79 10 2 8.21 2 6C2 3.79 3.79 2 6 2C7.35 2 8.54 2.65 9.24 3.65"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d="M8 2.5L9.24 3.65L8 4.8"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
        {metricsError && (
          <div style={{
            background: 'var(--danger-bg)',
            border: '1px solid #f5c6c2',
            borderRadius: 'var(--radius)',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: 'var(--danger)'
          }}>
            Failed to load dashboard metrics: {metricsError}
            <button
              onClick={() => fetchDashboardMetrics()}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '13px'
              }}
            >
              Retry
            </button>
          </div>
        )}
        <div className="kpi-strip">
          <div className="kpi">
            <div className="kpi-label">Governed Requests Today</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' : dashboardMetrics?.governed_requests_today?.toLocaleString() || '—'}
            </div>
            <div className="kpi-sub">All access log entries · today</div>
          </div>
          <div className="kpi info">
            <div className="kpi-label">Active Consumers</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' : dashboardMetrics?.active_consumers?.toLocaleString() || '—'}
            </div>
            <div className="kpi-sub">Unique systems, workflows, and users accessing governed data today</div>
          </div>
          <div className="kpi success">
            <div className="kpi-label">Successful Requests</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' : dashboardMetrics?.successful_requests?.toLocaleString() || '—'}
            </div>
            <div className="kpi-sub">Requests completed successfully today</div>
          </div>
          <div className="kpi danger">
            <div className="kpi-label">Restricted / Failed</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' : dashboardMetrics?.restricted_failed?.toLocaleString() || '—'}
            </div>
            <div className="kpi-sub">Requests restricted, rate limited, or failed today</div>
          </div>
          <div className="kpi warn">
            <div className="kpi-label">Average Trust Score</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' : dashboardMetrics?.average_trust_score || '—'}
            </div>
            <div className="kpi-sub">Average trust and data quality confidence across responses</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Avg Response Time</div>
            <div className="kpi-value">
              {metricsLoading ? '—' : metricsError ? '—' :
                dashboardMetrics?.avg_response_time_ms ? (
                  <>
                    {dashboardMetrics.avg_response_time_ms}
                    <span style={{fontSize:'14px',fontWeight:500}}>ms</span>
                  </>
                ) : '—'
              }
            </div>
            <div className="kpi-sub">Average response delivery time</div>
          </div>
        </div>

        {/* Recent Governed Consumption */}
        <div className="section-label">Recent Governed Consumption</div>
        <div className="card section-gap">
          <div className="card-head">
            <div className="card-head-left">
              <div className="card-title">Governed Data Requests</div>
              <div className="card-sub">
                {requestsLoading
                  ? 'Loading requests...'
                  : requestsError
                  ? 'Error loading requests'
                  : Object.values(filters).some(v => v !== '')
                  ? `Showing ${paginatedConsumptionRows.length} of ${requestsPagination.total_records} requests · Filtered · Page ${requestsPagination.page} of ${requestsPagination.total_pages}`
                  : `Showing ${paginatedConsumptionRows.length} of ${requestsPagination.total_records} requests · Page ${requestsPagination.page} of ${requestsPagination.total_pages}`
                }
              </div>
            </div>
          </div>

{rolesError && (
            <div style={{
              background: 'var(--danger-bg)',
              border: '1px solid #f5c6c2',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              margin: '12px 22px 0',
              fontSize: '13px',
              color: 'var(--danger)'
            }}>
              Failed to load access roles: {rolesError}
              <button
                onClick={fetchApiKeyRoles}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px'
                }}
              >
                Retry
              </button>
            </div>
          )}

          {requestsError && (
            <div style={{
              background: 'var(--danger-bg)',
              border: '1px solid #f5c6c2',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              margin: '12px 22px 0',
              fontSize: '13px',
              color: 'var(--danger)'
            }}>
              Failed to load recent requests: {requestsError}
              <button
                onClick={() => fetchRecentRequests()}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px'
                }}
              >
                Retry
              </button>
            </div>
          )}

          <div className="filter-bar">
            <span className="filter-label">Filter:</span>
            <select
              className="filter-select"
              value={filters.role}
              onChange={(e) => setFilters({...filters, role: e.target.value})}
              disabled={rolesLoading}
            >
              <option value="">
                {rolesLoading ? 'Loading roles...' : 'All Access Roles'}
              </option>
              {apiKeyRoles.map((role) => (
                <option key={role.id} value={role.name}>
                  {role.description}
                </option>
              ))}
            </select>
            <select className="filter-select" value={filters.trust} onChange={(e) => setFilters({...filters, trust: e.target.value})}>
              <option value="">All Trust Levels</option>
              <option value="high">High Trust</option>
              <option value="medium">Medium Trust</option>
              <option value="low">Low Trust</option>
              <option value="none">No Trust Score</option>
            </select>
            <select className="filter-select" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
              <option value="">All Statuses</option>
              <option value="Successful">Successful</option>
              <option value="Restricted">Restricted</option>
              <option value="Rate Limited">Rate Limited</option>
              <option value="Failed">Failed</option>
            </select>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:'32px'}}></th>
                  <th style={{width:'108px'}}>Time</th>
                  <th style={{width:'180px'}}>Consumer</th>
                  <th style={{width:'130px'}}>Role</th>
                  <th style={{width:'190px'}}>Data Requested</th>
                  <th style={{width:'82px'}}>Records</th>
                  <th style={{width:'74px'}}>Trust Sc.</th>
                  <th style={{width:'84px'}}>Trust Level</th>
                  <th style={{width:'100px'}}>Status</th>
                  <th style={{width:'82px'}}>Resp. Time</th>
                </tr>
              </thead>
              <tbody>
                {paginatedConsumptionRows.map((r, idx) => {
                  const av = getAvatarStyle(r.caller_id);
                  const ts = r.trust_score !== null ? Math.round(r.trust_score * 100) : '—';
                  // Use correlation_id as unique identifier for expansion state
                  const rowId = `${r.correlation_id}-${idx}`;
                  const isExpanded = expandedRows.has(rowId);
                  return (
                    <React.Fragment key={rowId}>
                      <tr>
                        <td><button className="expand-btn" onClick={() => toggleRowById(rowId)}>{isExpanded ? '▼' : '▶'}</button></td>
                        <td style={{whiteSpace:'nowrap',color:'var(--muted)',fontSize:'12px'}}>{formatUSDateTime(r.accessed_at)}</td>
                        <td>
                          <div className="chip">
                            <div className="avatar" style={{background:av.bg,color:av.fg}}>{av.i}</div>
                            <div><div className="chip-name">{truncateText(r.caller_id)}</div><div className="chip-sub">{roleLabels[r.role] || r.role}</div></div>
                          </div>
                        </td>
                        <td style={{fontSize:'12px'}}>{roleLabels[r.role] || r.role}</td>
                        <td style={{fontSize:'12px'}}>{resourceLabels[r.resource_type] || r.resource_type} <span style={{fontSize:'11px',color:'var(--muted)'}}>({truncateText(r.resource_ids, 15)})</span></td>
                        <td style={{textAlign:'center'}}>{r.records_returned}</td>
                        <td style={{textAlign:'center',fontWeight:700}}>{ts}</td>
                        <td>{trustBadge(r.trust_level)}</td>
                        <td>{statusBadge(r.status_code)}</td>
                        <td style={{color: r.response_time_ms > 1000 ? 'var(--danger)' : r.response_time_ms > 400 ? 'var(--warn)' : ''}}>{r.response_time_ms}ms</td>
                      </tr>
                      {isExpanded && (
                        <tr className="request-detail-row">
                          <td colSpan={10}>
                            <div className="detail-inner open">
                              <div className="detail-section-label">Request Detail &amp; Traceability</div>
                              <div className="detail-grid-divider"></div>
                              <div className="detail-grid">
                                <div className="detail-field"><label>Correlation ID</label><span>{truncateText(r.correlation_id || 'N/A', 20)}</span></div>
                                <div className="detail-field"><label>Query Filters</label><span>{truncateText(r.query_filters || 'No filters applied', 25)}</span></div>
                                <div className="detail-field"><label>Query Complexity</label><span>{r.query_complexity_score || 'N/A'}</span></div>
                                <div className="detail-field"><label>Caller Type</label><span>{truncateText(r.caller_type || 'N/A')}</span></div>
                              </div>
                              <div className="detail-grid">
                                <div className="detail-field"><label>IP Address</label><span>{r.ip_address || 'N/A'}</span></div>
                                <div className="detail-field"><label>User Agent</label><span>{truncateText(r.user_agent || 'N/A', 30)}</span></div>
                                {r.error_message && <div className="detail-field"><label>Error Message</label><span className="detail-error">{truncateText(r.error_message, 40)}</span></div>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination for Consumption Table */}
          <div style={{padding: '16px 22px', borderTop: '1px solid var(--border)', background: '#fafbfc'}}>
            <PaginationControls
              currentPage={requestsPagination.page}
              totalPages={requestsPagination.total_pages}
              onPageChange={(page) => {
                setExpandedRows(new Set()); // Clear expanded rows when page changes
                fetchRecentRequests(page);
              }}
            />
          </div>
        </div>

        {/* Trust & Data Quality Summary */}
        <div className="section-label">Trust &amp; Data Quality</div>
        <div className="card section-gap">
          <div className="card-head">
            <div className="card-head-left">
              <div className="card-title">Response Trust Context</div>
              <div className="card-sub">Trust signals are returned with every governed API response. Consumers can use these to understand the reliability and freshness of the data they are acting on.</div>
            </div>
          </div>

          {trustQualityError && (
            <div style={{
              background: 'var(--danger-bg)',
              border: '1px solid #f5c6c2',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              margin: '12px 22px 0',
              fontSize: '13px',
              color: 'var(--danger)'
            }}>
              Failed to load trust quality metrics: {trustQualityError}
              <button
                onClick={() => fetchTrustQualityMetrics()}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px'
                }}
              >
                Retry
              </button>
            </div>
          )}

          <div className="card-body">
            <div className="trust-row">
              <div className="trust-tile">
                <div className="trust-tile-label">Average Trust Score</div>
                <div className="trust-tile-value">
                  {trustQualityLoading ? '—' : trustQualityError ? '—' : trustQualityMetrics?.average_trust_score || '—'}
                </div>
                <div className="trust-tile-sub">Average data quality confidence across all responses today</div>
                <div className="bar-wrap">
                  <div
                    className="bar bar-green"
                    style={{
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.average_trust_score || 0}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="trust-tile">
                <div className="trust-tile-label">Average Completeness</div>
                <div className="trust-tile-value">
                  {trustQualityLoading ? '—' : trustQualityError ? '—' :
                    trustQualityMetrics?.average_completeness_pct ? `${trustQualityMetrics.average_completeness_pct}%` : '—'
                  }
                </div>
                <div className="trust-tile-sub">Average field completeness across all responses</div>
                <div className="bar-wrap">
                  <div
                    className="bar bar-green"
                    style={{
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.average_completeness_pct || 0}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="trust-tile">
                <div className="trust-tile-label">Validation Pass Rate</div>
                <div className="trust-tile-value">
                  {trustQualityLoading ? '—' : trustQualityError ? '—' :
                    trustQualityMetrics?.validation_pass_rate ? `${trustQualityMetrics.validation_pass_rate}%` : '—'
                  }
                </div>
                <div className="trust-tile-sub">Average data quality rule pass rate across all responses</div>
                <div className="bar-wrap">
                  <div
                    className="bar bar-green"
                    style={{
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.validation_pass_rate || 0}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="trust-tile">
                <div className="trust-tile-label">Trust Level Distribution</div>
                <div className="trust-tile-value" style={{fontSize:'13px',fontWeight:600,color:'var(--muted)'}}>
                  {trustQualityLoading ? '—' : trustQualityError ? '—' :
                    trustQualityMetrics?.trust_level_distribution.total_responses?.toLocaleString() || '—'
                  } responses
                </div>
                <div className="trust-tile-sub">High · Medium · Low</div>
                <div style={{marginTop:'10px',display:'flex',gap:'4px',alignItems:'center'}}>
                  <div
                    style={{
                      height:'8px',
                      borderRadius:'4px',
                      background:'var(--success)',
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.trust_level_distribution.high_percentage || 0}%`
                    }}
                    title={`High Trust: ${trustQualityMetrics?.trust_level_distribution.high_percentage || 0}%`}
                  ></div>
                  <div
                    style={{
                      height:'8px',
                      borderRadius:'4px',
                      background:'var(--warn)',
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.trust_level_distribution.medium_percentage || 0}%`
                    }}
                    title={`Medium Trust: ${trustQualityMetrics?.trust_level_distribution.medium_percentage || 0}%`}
                  ></div>
                  <div
                    style={{
                      height:'8px',
                      borderRadius:'4px',
                      background:'var(--danger)',
                      width: trustQualityLoading || trustQualityError ? '0%' : `${trustQualityMetrics?.trust_level_distribution.low_percentage || 0}%`
                    }}
                    title={`Low Trust: ${trustQualityMetrics?.trust_level_distribution.low_percentage || 0}%`}
                  ></div>
                </div>
              </div>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Avg Trust Score</th>
                    <th>Trust Level</th>
                    <th>Completeness</th>
                    <th>Validation Pass Rate</th>
                    <th>Data Freshness</th>
                  </tr>
                </thead>
                <tbody>
                  {trustQualityLoading ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                        Loading trust quality data...
                      </td>
                    </tr>
                  ) : trustQualityError ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px'}}>
                        <div style={{color:'var(--danger)',marginBottom:'8px'}}>Failed to load trust quality data</div>
                        <div style={{fontSize:'12px',color:'var(--muted)',marginBottom:'12px'}}>{trustQualityError}</div>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => fetchTrustQualityMetrics()}
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : trustQualityMetrics?.data_type_breakdown?.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                        No data type breakdown available
                      </td>
                    </tr>
                  ) : (
                    trustQualityMetrics?.data_type_breakdown?.map((item, idx) => {
                      const trustBadgeClass = item.trust_level?.toLowerCase().includes('high') ? 'b-t-high'
                                            : item.trust_level?.toLowerCase().includes('medium') ? 'b-t-med'
                                            : 'b-t-low';
                      const trustBadgeLabel = item.trust_level?.toLowerCase().includes('high') ? 'High Trust'
                                            : item.trust_level?.toLowerCase().includes('medium') ? 'Medium Trust'
                                            : 'Low Trust';

                      // Format the last updated time - handle both date strings and relative time (e.g., "33 min ago", "5 days ago", "2 months ago")
                      const formatTime = (dateString: string) => {
                        try {
                          // Check if it's a relative time format like "33 min ago", "5 days ago", "2 months ago", etc.
                          const relativeTimeMatch = dateString.match(/(\d+)\s*(min|minute|hour|day|week|month|year)s?\s*ago/i);
                          if (relativeTimeMatch) {
                            const amount = parseInt(relativeTimeMatch[1]);
                            const unit = relativeTimeMatch[2].toLowerCase();
                            const actualTime = new Date();

                            // Calculate actual time based on unit
                            switch (unit) {
                              case 'min':
                              case 'minute':
                                actualTime.setMinutes(actualTime.getMinutes() - amount);
                                break;
                              case 'hour':
                                actualTime.setHours(actualTime.getHours() - amount);
                                break;
                              case 'day':
                                actualTime.setDate(actualTime.getDate() - amount);
                                break;
                              case 'week':
                                actualTime.setDate(actualTime.getDate() - (amount * 7));
                                break;
                              case 'month':
                                actualTime.setMonth(actualTime.getMonth() - amount);
                                break;
                              case 'year':
                                actualTime.setFullYear(actualTime.getFullYear() - amount);
                                break;
                            }

                            // Format based on how long ago it was
                            const now = new Date();
                            const diffInDays = Math.floor((now.getTime() - actualTime.getTime()) / (1000 * 60 * 60 * 24));

                            if (diffInDays === 0) {
                              // Same day - show time
                              return actualTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              }) + ' today';
                            } else if (diffInDays === 1) {
                              // Yesterday - show time + yesterday
                              return actualTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              }) + ' yesterday';
                            } else if (diffInDays < 7) {
                              // This week - show day of week + time
                              return actualTime.toLocaleDateString('en-US', {
                                weekday: 'short'
                              }) + ' ' + actualTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              });
                            } else {
                              // Older - show full date
                              return actualTime.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: actualTime.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                              });
                            }
                          }

                          // Handle regular date strings
                          const date = new Date(dateString);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            });
                          }

                          // Fallback to original string
                          return dateString;
                        } catch {
                          return dateString;
                        }
                      };

                      return (
                        <tr key={idx}>
                          <td><strong>{truncateText(item.data_type, 15)}</strong></td>
                          <td>{item.avg_trust_score}</td>
                          <td><span className={`badge ${trustBadgeClass}`}>{trustBadgeLabel}</span></td>
                          <td>{item.completeness_pct}%</td>
                          <td>{item.validation_pass_rate}%</td>
                          <td>{truncateText(formatTime(item.data_freshness), 12)}</td>
                        </tr>
                      );
                    }) || (
                      <tr>
                        <td colSpan={6} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                          No data available
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Governed Consumer Access */}
        <div className="section-label">Governed Consumer Access</div>
        <div className="card section-gap">
          <div className="card-head">
            <div className="card-head-left">
              <div className="card-title">Governed Consumer Access</div>
              <div className="card-sub">
                {consumersLoading
                  ? 'Loading registered consumers...'
                  : consumersError
                  ? 'Error loading consumers'
                  : `Showing ${consumerList.length} of ${pagination.total_records} registered consumers · Page ${pagination.page} of ${pagination.total_pages}`
                }
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
              <button className="btn btn-primary btn-sm" onClick={openDrawer}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Register Consumer
              </button>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:'32px'}}></th>
                  <th style={{width:'250px'}}>Consumer / Key Name</th>
                  <th style={{width:'180px'}}>Caller Role</th>
                  <th style={{width:'120px'}}>Caller Type</th>
                  <th style={{width:'120px'}}>Daily Limit</th>
                  <th style={{width:'130px'}}>Created By</th>
                  <th style={{width:'140px'}}>Expires</th>
                  <th style={{width:'130px'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {consumersLoading ? (
                  <tr>
                    <td colSpan={8} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                      Loading API keys...
                    </td>
                  </tr>
                ) : consumersError ? (
                  <tr>
                    <td colSpan={8} style={{textAlign:'center',padding:'40px'}}>
                      <div style={{color:'var(--danger)',marginBottom:'8px'}}>Failed to load API keys</div>
                      <div style={{fontSize:'12px',color:'var(--muted)',marginBottom:'12px'}}>{consumersError}</div>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => fetchApiKeys()}
                      >
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : consumerList.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                      No API keys found
                    </td>
                  </tr>
                ) : (
                  consumerList.map((c, idx) => {
                  const name = consumerDisplayName(c);
                  const av = getAvatarStyle(name);
                  const status = expirationStatus(c.expires_at);
                  const isExpanded = expandedConsumers.has(idx);
                  return (
                    <React.Fragment key={idx}>
                      <tr>
                        <td><button className="expand-btn" onClick={() => toggleConsumer(idx)}>{isExpanded ? '▼' : '▶'}</button></td>
                        <td>
                          <div className="chip">
                            <div className="avatar" style={{background:av.bg,color:av.fg}}>{av.i}</div>
                            <div>
                              <div className="chip-name">
                                {truncateText(name)}
                              </div>
                              <div className="chip-sub">
                                {truncateText(c.key_id)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{fontSize:'12px'}}>
                          {(() => {
                            const roleName = c.role_name || '';
                            const fullRoleName = roleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                            return truncateText(fullRoleName);
                          })()}
                        </td>
                        <td style={{fontSize:'12px'}}>
                          {truncateText("API Key")}
                        </td>
                        <td style={{fontSize:'12px',whiteSpace:'nowrap'}}>
                          {(() => {
                            const dailyText = `${Number(c.daily_quota || 0).toLocaleString()} / day`;
                            return truncateText(dailyText);
                          })()}
                        </td>
                        <td style={{fontSize:'12px'}}>
                          {truncateText(c.created_by)}
                        </td>
                        <td style={{fontSize:'12px',whiteSpace:'nowrap'}}>
                          {truncateText(c.expires_at || '—')}
                        </td>
                        <td style={{whiteSpace:'nowrap'}}>{consumerStatusBadge(status)}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="consumer-detail-row">
                          <td colSpan={8}>
                            <div className="consumer-detail-inner open">
                              <div className="detail-section-label">Consumer Detail</div>
                              <div className="detail-grid">
                                <div className="detail-field"><label>Key ID</label><span style={{fontFamily:'monospace',fontSize:'11px'}}>{truncateText(c.key_id)}</span></div>
                                <div className="detail-field"><label>Role Name</label><span style={{fontFamily:'monospace',fontSize:'11px'}}>{truncateText(c.role_name)}</span></div>
                                <div className="detail-field"><label>Owner Email</label><span style={{fontFamily:'monospace',fontSize:'11px'}}>{truncateText(c.owner_email)}</span></div>
                                <div className="detail-field"><label>Status</label><span>{truncateText(c.status)}</span></div>
                                <div className="detail-field"><label>Created At</label><span>{truncateText(new Date(c.created_at).toLocaleDateString())}</span></div>
                                <div className="detail-field"><label>Created By</label><span>{truncateText(c.created_by)}</span></div>
                                <div className="detail-field"><label>Expires At</label><span>{truncateText(c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—')}</span></div>
                                <div className="detail-field"><label>Daily Quota</label><span>{truncateText(Number(c.daily_quota).toLocaleString())}</span></div>
                                {c.key_metadata?.organization && (
                                  <div className="detail-field"><label>Organization</label><span>{truncateText(c.key_metadata.organization)}</span></div>
                                )}
                                {c.last_used_at && (
                                  <div className="detail-field"><label>Last Used</label><span>{truncateText(new Date(c.last_used_at).toLocaleString())}</span></div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination for Consumer Access Table */}
          {!consumersLoading && !consumersError && consumerList.length > 0 && (
            <div style={{padding: '16px 22px', borderTop: '1px solid var(--border)', background: '#fafbfc'}}>
              <PaginationControls
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                onPageChange={fetchApiKeys}
              />
            </div>
          )}
        </div>

        {/* Restricted & Failed Requests */}
        <div className="section-label">Recent Restricted &amp; Failed Requests</div>
        <div className="card">
          <div className="card-head">
            <div className="card-head-left">
              <div className="card-title">Request Outcomes Requiring Attention</div>
              <div className="card-sub">
                {outcomesLoading
                  ? 'Loading request outcomes...'
                  : outcomesError
                  ? 'Error loading request outcomes'
                  : outcomesStatusFilter
                  ? `Showing ${requestOutcomes?.failed_requests?.length || 0} of ${outcomesPagination.total_records} filtered requests · Page ${outcomesPagination.page} of ${outcomesPagination.total_pages}`
                  : `Showing ${requestOutcomes?.failed_requests?.length || 0} of ${outcomesPagination.total_records} failed requests · Page ${outcomesPagination.page} of ${outcomesPagination.total_pages}`
                }
              </div>
            </div>
          </div>

          {outcomesError && (
            <div style={{
              background: 'var(--danger-bg)',
              border: '1px solid #f5c6c2',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              margin: '12px 22px 0',
              fontSize: '13px',
              color: 'var(--danger)'
            }}>
              Failed to load request outcomes: {outcomesError}
              <button
                onClick={() => fetchRequestOutcomes()}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px'
                }}
              >
                Retry
              </button>
            </div>
          )}

          <div className="card-body">
            <div className="restricted-grid" style={{marginBottom:'22px'}}>
              <div className="restricted-tile rt-danger">
                <div className="rt-icon">🚫</div>
                <div>
                  <div className="rt-count">
                    {outcomesLoading ? '—' : outcomesError ? '—' : requestOutcomes?.summary?.restricted_count?.toLocaleString() || '0'}
                  </div>
                  <div className="rt-label">Restricted</div>
                  <div className="rt-sub">
                    status_code = 403 · {outcomesLoading ? '—' : outcomesError ? '—' : `${requestOutcomes?.summary?.restricted_percentage || 0}% of requests`}
                  </div>
                </div>
              </div>
              <div className="restricted-tile rt-warn">
                <div className="rt-icon">⏱</div>
                <div>
                  <div className="rt-count">
                    {outcomesLoading ? '—' : outcomesError ? '—' : requestOutcomes?.summary?.rate_limited_count?.toLocaleString() || '0'}
                  </div>
                  <div className="rt-label">Rate Limited</div>
                  <div className="rt-sub">
                    status_code = 429 · {outcomesLoading ? '—' : outcomesError ? '—' : `${requestOutcomes?.summary?.rate_limited_percentage || 0}% of requests`}
                  </div>
                </div>
              </div>
              <div className="restricted-tile rt-error">
                <div className="rt-icon">⚠️</div>
                <div>
                  <div className="rt-count">
                    {outcomesLoading ? '—' : outcomesError ? '—' : requestOutcomes?.summary?.error_count?.toLocaleString() || '0'}
                  </div>
                  <div className="rt-label">Error</div>
                  <div className="rt-sub">
                    status_code = 500 · {outcomesLoading ? '—' : outcomesError ? '—' : `${requestOutcomes?.summary?.error_percentage || 0}% of requests`}
                  </div>
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div className="filter-bar" style={{margin: '0 0 22px 0'}}>
              <span className="filter-label">Filter:</span>
              <select
                className="filter-select"
                value={outcomesStatusFilter}
                onChange={(e) => setOutcomesStatusFilter(e.target.value)}
                disabled={outcomesLoading}
              >
                <option value="">All Request Outcomes</option>
                <option value="Restricted">Restricted (403)</option>
                <option value="Rate Limited">Rate Limited (429)</option>
                <option value="Error">Error (500)</option>
              </select>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Consumer</th>
                    <th>Role</th>
                    <th>Data Requested</th>
                    <th>Status</th>
                    <th>Error Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomesLoading ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                        Loading request outcomes...
                      </td>
                    </tr>
                  ) : outcomesError ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px'}}>
                        <div style={{color:'var(--danger)',marginBottom:'8px'}}>Failed to load request outcomes</div>
                        <div style={{fontSize:'12px',color:'var(--muted)',marginBottom:'12px'}}>{outcomesError}</div>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => fetchRequestOutcomes()}
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : !requestOutcomes?.failed_requests?.length ? (
                    <tr>
                      <td colSpan={6} style={{textAlign:'center',padding:'40px',color:'var(--muted)'}}>
                        No failed requests found
                      </td>
                    </tr>
                  ) : (
                    requestOutcomes.failed_requests.map((request, idx) => {
                      // Get avatar style using consumer_code as fallback for consumer_name
                      const av = getAvatarStyle(request.consumer_name);

                      // Create status badge based on status string
                      const getFailedStatusBadge = (status: string) => {
                        if (status === 'Restricted') return <span className="badge b-danger">Restricted</span>;
                        if (status === 'Rate Limited') return <span className="badge b-warn">Rate Limited</span>;
                        if (status === 'Error') return <span className="badge b-danger">Error</span>;
                        return <span className="badge b-neutral">{status}</span>;
                      };

                      return (
                        <tr key={idx}>
                          <td style={{whiteSpace:'nowrap',color:'var(--muted)',fontSize:'12px'}}>{formatUSDateTime(request.time)}</td>
                          <td>
                            <div className="chip">
                              <div className="avatar" style={{background:av.bg,color:av.fg}}>{request.consumer_code}</div>
                              <div>
                                <div className="chip-name">{truncateText(request.consumer_name)}</div>
                                <div className="chip-sub">{roleLabels[request.role] || request.role}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{fontSize:'12px'}}>{roleLabels[request.role] || request.role}</td>
                          <td style={{fontSize:'12px'}}>{truncateText(request.data_requested, 20)}</td>
                          <td>{getFailedStatusBadge(request.status)}</td>
                          <td style={{fontSize:'12px',color:'var(--danger)'}}>{truncateText(request.error_detail || '—', 25)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination for Request Outcomes Table */}
            {!outcomesLoading && !outcomesError && (requestOutcomes?.failed_requests?.length ?? 0) > 0 && (
              <div style={{padding: '16px 22px', borderTop: '1px solid var(--border)', background: '#fafbfc'}}>
                <PaginationControls
                  currentPage={outcomesPagination.page}
                  totalPages={outcomesPagination.total_pages}
                  onPageChange={(page) => fetchRequestOutcomes(page, outcomesStatusFilter)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Register Consumer Drawer */}
      {isDrawerOpen && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="drawer" role="dialog" aria-modal="true">
            <div className="drawer-head">
              <div>
                <div className="drawer-title">Register Consumer</div>
                <div className="drawer-sub">Create a visible consumer/key record for governed ClientConverge access.</div>
              </div>
              <button className="drawer-close" onClick={closeDrawer} aria-label="Close">✕</button>
            </div>

            <div className="drawer-body">
              <div className="form-section">
                <div className="form-section-label">Consumer Identity</div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-name">
                    Consumer / Key Name <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-name"
                    type="text"
                    placeholder="e.g. ACME Financial Advisor Portal"
                    value={newConsumerForm.name}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, name: e.target.value}))}
                  />
                  <div className="form-hint">Visible identifier for the consumer. Do not enter secrets or tokens.</div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-description">
                    Description <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-description"
                    type="text"
                    placeholder="e.g. API key for ACME's advisor portal to access client data"
                    value={newConsumerForm.description}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, description: e.target.value}))}
                  />
                  <div className="form-hint">Brief description of what this API key will be used for.</div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-caller-role">
                    Caller Role <span className="req">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="f-caller-role"
                    value={newConsumerForm.callerRole}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, callerRole: e.target.value}))}
                    disabled={rolesLoading}
                  >
                    <option value="" disabled>
                      {rolesLoading ? 'Loading roles...' : 'Select caller role'}
                    </option>
                    {apiKeyRoles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.description}
                      </option>
                    ))}
                  </select>
                  {rolesError && (
                    <div className="form-hint" style={{color: 'var(--danger)', marginTop: '4px'}}>
                      {rolesError}
                    </div>
                  )}
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-owner-email">
                    Owner Email <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-owner-email"
                    type="email"
                    placeholder="e.g. integration@acme-financial.com"
                    value={newConsumerForm.ownerEmail}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, ownerEmail: e.target.value}))}
                  />
                  <div className="form-hint">Email address of the person responsible for this API key.</div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-label">Access Limits</div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-daily-quota">
                    Daily Limit <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-daily-quota"
                    type="number"
                    min="1"
                    placeholder="e.g. 5000"
                    value={newConsumerForm.dailyQuota}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, dailyQuota: e.target.value}))}
                  />
                  <div className="form-hint">Maximum daily request volume for this consumer.</div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-expires-in-days">
                    Expires In Days <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-expires-in-days"
                    type="number"
                    min="1"
                    max="3650"
                    placeholder="365"
                    value={newConsumerForm.expiresInDays}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, expiresInDays: e.target.value}))}
                  />
                  <div className="form-hint">Number of days until this API key expires (1-3650 days).</div>
                </div>
              </div>

              <div className="form-section" style={{marginBottom:0}}>
                <div className="form-section-label">Organization Details</div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-organization">
                    Organization <span className="req">*</span>
                  </label>
                  <input
                    className="form-input"
                    id="f-organization"
                    type="text"
                    placeholder="e.g. ACME Financial"
                    value={newConsumerForm.organization}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, organization: e.target.value}))}
                  />
                  <div className="form-hint">Name of the organization using this API key.</div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-environment">
                    Environment <span className="req">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="f-environment"
                    value={newConsumerForm.environment}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, environment: e.target.value}))}
                  >
                    <option value="production">Production</option>
                    <option value="staging">Staging</option>
                    <option value="development">Development</option>
                    <option value="testing">Testing</option>
                  </select>
                  <div className="form-hint">Environment where this API key will be used.</div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="f-contact-person">Contact Person</label>
                  <input
                    className="form-input"
                    id="f-contact-person"
                    type="text"
                    placeholder="e.g. John Doe"
                    value={newConsumerForm.contactPerson}
                    onChange={(e) => setNewConsumerForm(prev => ({...prev, contactPerson: e.target.value}))}
                  />
                  <div className="form-hint">Name of the primary contact person for this integration.</div>
                </div>

                <div className="form-field">
                  <label className="form-label">Caller Type</label>
                  <div className="auto-field">
                    <span>API Key</span>
                    <span style={{marginLeft:'auto',fontSize:'11px',color:'var(--muted)'}}>Fixed</span>
                  </div>
                  <div className="form-hint" style={{marginTop:'6px'}}>All registrations use API Key authentication.</div>
                </div>

                <div className="form-field">
                  <label className="form-label">Created By</label>
                  <div className="auto-field">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="7" cy="5" r="3" stroke="#90aecb" strokeWidth="1.5"/>
                      <path d="M1.5 12.5c0-2.485 2.462-4.5 5.5-4.5s5.5 2.015 5.5 4.5" stroke="#90aecb" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Admin</span>
                    <span style={{marginLeft:'auto',fontSize:'11px',color:'var(--muted)'}}>Auto-populated</span>
                  </div>
                </div>
              </div>
            </div>

            {creationError && (
              <div className="creation-error">
                <div className="error-icon">❌</div>
                <div>
                  <div className="error-title">Failed to Create API Key</div>
                  <div className="error-message">{creationError}</div>
                </div>
              </div>
            )}

            <div className="drawer-footer">
              <button className="btn btn-ghost" onClick={closeDrawer} disabled={isCreating}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={registerConsumer}
                disabled={isCreating || rolesLoading}
              >
                {isCreating ? 'Creating...' : 'Register Consumer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Success Modal */}
      {showApiKeyModal && createdApiKey && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeApiKeyModal()} style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="modal-dialog" role="dialog" aria-modal="true" style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius)',
            padding: '28px',
            width: '500px',
            maxWidth: '90vw',
            boxShadow: 'var(--shadow-md)',
            position: 'relative'
          }}>
            <div className="api-key-success">
              <div className="success-header">
                <div className="success-icon">✅</div>
                <div>
                  <div className="success-title">API Key Created Successfully!</div>
                  <div className="success-subtitle">{createdApiKey.data.api_key.name}</div>
                </div>
              </div>

              <div className="api-key-display">
                <label className="form-label">Your API Key</label>
                <div className="api-key-value">
                  <code>{createdApiKey.data.full_key}</code>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => navigator.clipboard.writeText(createdApiKey.data.full_key)}
                  >
                    Copy
                  </button>
                </div>
                <div className="security-notice">
                  ⚠️ {createdApiKey.data.security_notice}
                </div>
              </div>

              <div className="key-details">
                <div className="detail-row">
                  <span>Key ID:</span>
                  <span>{createdApiKey.data.api_key.key_id}</span>
                </div>
                <div className="detail-row">
                  <span>Role:</span>
                  <span>{createdApiKey.data.api_key.role_name}</span>
                </div>
                <div className="detail-row">
                  <span>Daily Quota:</span>
                  <span>{createdApiKey.data.api_key.daily_quota.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span>Expires:</span>
                  <span>{new Date(createdApiKey.data.api_key.expires_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div style={{display: 'flex', justifyContent: 'center', marginTop: '24px'}}>
                <button className="btn btn-primary" onClick={closeApiKeyModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Tooltip */}
      {hoverPopup.show && (
        <div
          style={{
            position: 'fixed',
            left: hoverPopup.x,
            top: hoverPopup.y,
            transform: 'translate(-50%, calc(-100% - 12px))',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '13px',
            fontWeight: '500',
            color: '#374151',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 1000,
            maxWidth: '300px',
            wordWrap: 'break-word',
            pointerEvents: 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            lineHeight: '1.4',
          }}
        >
          {hoverPopup.text}
          {/* Tooltip Arrow */}
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              width: '12px',
              height: '12px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderTop: 'none',
              borderLeft: 'none',
              transform: 'translateX(-50%) rotate(45deg)',
            }}
          />
        </div>
      )}
    </>
  );
};

export default EnterpriseConsumptionConsole;
